import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { v4 as uuidv4 } from 'uuid';

import {
  VITE_API_GATEWAY,
  VITE_API_REQUEST_TIMEOUT_MS,
  VITE_AUTH0_AUDIENCE,
} from '~/lib/constants/env';

import mocks from './mocks';
import Scribe, { Action } from './Scribe';
import util from './util/util';

export enum ApiCommand {
  GET = 'get',
  PATCH = 'patch',
  POST = 'post',
  PUT = 'put',
  DELETE = 'delete',
}

export const BaseApiConfig = {
  auth0Audience: VITE_AUTH0_AUDIENCE,
  baseUrl: VITE_API_GATEWAY,
  timeout: Number.parseInt(VITE_API_REQUEST_TIMEOUT_MS ?? '30000', 10),
};

class Api {
  static readonly encodedAmpersand: string = encodeURIComponent('&');

  static readonly axiosConfig: AxiosRequestConfig = {
    baseURL: BaseApiConfig.baseUrl,
    timeout: BaseApiConfig.timeout,
    // withCredentials: true, TODO: enable when CORS is properly configured, needed to send cookies
  };

  static readonly instance: AxiosInstance = axios.create(Api.axiosConfig);

  static readonly scribeInstance: AxiosInstance = axios.create(Api.axiosConfig);

  /**
   * Init function to set runtime configurations such as interceptors.
   */
  static init(): void {
    const { request, response } = Api.instance.interceptors;
    request.use((config: InternalAxiosRequestConfig) => {
      const { method, data, headers, url } = config;
      const options = typeof data === 'string' ? util.parseJson(data) : data;
      const token = Scribe.extractToken(
        (headers.Authorization as string) ?? '',
      );

      Scribe.post({
        action: Action.REQUESTED,
        httpClient: Api.scribeInstance,
        method,
        options,
        token,
        url,
      });

      return config;
    });

    response.use((res: AxiosResponse) => {
      const { method, headers, url } = res.config;
      const token = Scribe.extractToken(
        (headers.Authorization as string) ?? '',
      );

      Scribe.post({
        action: Action.RECEIVED,
        httpClient: Api.scribeInstance,
        method,
        options: {},
        token,
        url,
      });

      return res;
    });
  }

  /**
   * Get function for retrieving the client explicitly.
   */
  static get client(): AxiosInstance {
    return Api.instance;
  }

  static getEndpoint(
    command: string,
    url: string,
    queryString: string,
    mock: boolean,
  ): string {
    try {
      return mock
        ? mocks[`${url}${queryString}`][command]
        : `${url}${queryString}`;
    } catch {
      if (mock) {
        throw new Error(
          `No mock API available for command \`${command}\` at URL \`${url}\``,
        );
      }
    }

    return '';
  }

  /**
   * Utility function that formats a key-value pair representing a single query param into the
   * format that the backend is expecting. The format is as follows:
   *
   * ```
   * key=value
   * ```
   *
   * where `value` can be any of the following:
   *
   * - value // a simple string
   * - 10 // an integer
   * - param1::value1%26param2::value2%26param2::value3... // a complex string represnting an array
   *
   * In the last example, the `::` character group delineates the param from its value, similar to
   * a JSON object's key to its value. The `%26` URI encoded character is the `&` (ampersand)
   * symbol in UTF, which is used to delineate the elements in the query param array.
   *
   * Example of calls to this function are as follows:
   *
   * buildQueryParam(['key', 'value']) –
   * Expected result: `key=value`
   *
   * buildQueryParam(['key', 10]) –
   * Expected result: `key=10`
   *
   * buildQueryParam(['key', {
   *   param1: 'value1',
   *   param2: 2,
   *   param3: ['value3', 'value4'],
   * }]) –
   * Expected result: `key=param1::value1%26param2::2%26param3::value3%26param3::value4`
   *
   * Notice that in the last example, the value of `param3`, which was an array, was flattened such
   * that `param3` was duplicated for each of its repspective values.
   *
   * @param _ Destructured tuple that represents the key-value pair of a query param.
   * @returns A string representing a single query param argument in requests (currently only GET).
   */
  static buildQueryParam([key, value]: [string, ApiOptionsValue]): string {
    if (typeof value === 'object') {
      if (!Array.isArray(value)) {
        const param = Object.entries(value)
          .map(([paramKey, paramValue]) => {
            if (Array.isArray(paramValue)) {
              return paramValue
                .map((val: boolean | number | string) => `${paramKey}::${val}`)
                .join(Api.encodedAmpersand);
            }

            return `${paramKey}::${paramValue}`;
          })
          .join(Api.encodedAmpersand);

        return `${key}=${param}`;
      }
      // TODO: Potentially account for array type in the future
    }

    return `${key}=${value}`;
  }

  /**
   * Build the full query string with query params in the request.
   *
   * @param url The path relative to the gateway URL (i.e. '/users').
   * @param queryParams Object containing query params for a GET request.
   * @returns The full query string used to fetch data.
   */
  static buildRequestString(
    command: ApiCommand,
    url: string,
    queryParams: Record<string, ApiOptionsValue> = {},
    mock = false,
  ): string {
    let queryString = Object.entries(queryParams)
      .map(Api.buildQueryParam)
      .join('&');
    if (queryString) queryString = `?${queryString}`;
    return Api.getEndpoint(command, url, queryString, mock);
  }

  static getDefaultHeaders(token: string): Record<string, string> {
    return {
      Authorization: `Bearer ${token}`,
      uniqueRequestId: `Maud:${uuidv4()}`,
    };
  }

  /**
   * DELETE request handler.
   *
   * @param token Access token generated via Auth0 to authorize the request.
   * @param url The path relative to the gateway URL (i.e. '/users').
   * @param queryParams Object containing query params for a DELETE request.
   * @returns The data from the DELETE request.
   */
  static async delete<T>(
    token: string,
    url: string,
    queryParams: Record<string, ApiOptionsValue> = {},
    mock = false,
  ): Promise<ApiResponse<T>> {
    const queryString = Api.buildRequestString(
      ApiCommand.DELETE,
      url,
      queryParams,
      mock,
    );

    try {
      const response = await Api.client.delete<T>(queryString, {
        data: {},
        headers: {
          ...this.getDefaultHeaders(token),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      return {
        data: response.data,
        statusCode: response.status,
      };
    } catch (err) {
      return {
        error: err?.response?.data,
        statusCode: 500,
      };
    }
  }

  /**
   * GET request handler.
   *
   * @param token Access token generated via Auth0 to authorize the request.
   * @param url The path relative to the gateway URL (i.e. '/users').
   * @param queryParams Object containing query params for a GET request.
   * @returns The data from the GET request.
   */
  static async get<T>(
    token: string,
    url: string,
    queryParams: Record<string, ApiOptionsValue> = {},
    mock = false,
  ): Promise<ApiResponse<T>> {
    const queryString = Api.buildRequestString(
      ApiCommand.GET,
      url,
      queryParams,
      mock,
    );

    try {
      const response = await Api.client.get<T>(queryString, {
        headers: this.getDefaultHeaders(token),
      });

      return {
        data: response.data,
        statusCode: response.status,
      };
    } catch (err) {
      return {
        error: err?.response?.data,
        statusCode: 500,
      };
    }
  }

  /**
   * POST request handler.
   *
   * @param token Access token generated via Auth0 to authorize the request.
   * @param url The path relative to the gateway URL (i.e. '/users').
   * @param data Object containing the body of the POST request.
   * @returns The response from the POST request.
   */
  static async post<T>(
    token: string,
    url: string,
    options: Record<string, ApiOptionsValue> = {},
    mock = false,
  ): Promise<ApiResponse<T>> {
    const queryString = Api.buildRequestString(ApiCommand.POST, url, {}, mock);

    try {
      const { data: response, status } = await Api.client.post<T>(
        queryString,
        options,
        {
          headers: this.getDefaultHeaders(token),
        },
      );

      return {
        data: response,
        statusCode: status,
      };
    } catch (err) {
      return {
        error: err?.response?.data,
        statusCode: 500,
      };
    }
  }

  /**
   * PUT request handler.
   *
   * @param token Access token generated via Auth0 to authorize the request.
   * @param url The path relative to the gateway URL (i.e. '/users').
   * @param data Object containing the body of the PUT request.
   * @returns The response from the PUT request.
   */
  static async put<T>(
    token: string,
    url: string,
    options: Record<string, ApiOptionsValue> = {},
    mock = false,
  ): Promise<ApiResponse<T>> {
    const queryString = Api.buildRequestString(ApiCommand.PUT, url, {}, mock);

    try {
      const { data: response, status } = await Api.client.put<T>(
        queryString,
        options,
        {
          headers: this.getDefaultHeaders(token),
        },
      );

      return {
        data: response,
        statusCode: status,
      };
    } catch (err) {
      return {
        error: err?.response?.data,
        statusCode: 500,
      };
    }
  }

  /**
   * PATCH request handler.
   *
   * @param token Access token generated via Auth0 to authorize the request.
   * @param url The path relative to the gateway URL (i.e. '/users').
   * @param data Object containing the body of the PATCH request.
   * @returns The response from the PATCH request.
   */
  static async patch<T>(
    token: string,
    url: string,
    options: Record<string, ApiOptionsValue> = {},
    mock = false,
  ): Promise<ApiResponse<T>> {
    const queryString = Api.buildRequestString(ApiCommand.PATCH, url, {}, mock);

    try {
      const { data: response, status } = await Api.client.patch<T>(
        queryString,
        options,
        {
          headers: this.getDefaultHeaders(token),
        },
      );

      return {
        data: response,
        statusCode: status,
      };
    } catch (err) {
      return {
        error: err?.response?.data,
        statusCode: 500,
      };
    }
  }
}

export default Api;
